import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckIcon } from '@phosphor-icons/react';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { Spinner } from '@/components/Spinner';
import { useDebounce } from '@/hooks/useDebounce';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { SEARCH_DEBOUNCE_MS } from '@/constants/debounce';
import { Popover } from '@/components/Popover';
import { ScrollEndFade } from '@/components/ScrollEndFade';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
    value: string;
    label: string;
    icon?: React.ElementType;
}

export interface ComboboxOptionRenderMeta {
    isSelected: boolean;
}

interface ComboboxProps {
    options: ComboboxOption[];
    selected: string[];
    onToggle: (value: string) => void;
    onClear?: () => void;
    onSearch?: (query: string) => void;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoading?: boolean;
    autoFocus?: boolean;
    title?: string;
    titleAction?: ReactNode;
    searchPlaceholder?: string;
    emptyText?: string;
    clearLabel?: string;
    className?: string;
    autoFocusSearch?: boolean;
    listClassName?: string;
    renderOption?: (
        option: ComboboxOption,
        meta: ComboboxOptionRenderMeta,
    ) => ReactNode;
}

export function Combobox({
    options,
    selected,
    onToggle,
    onClear,
    onSearch,
    onLoadMore,
    hasMore = false,
    isLoading = false,
    autoFocus = false,
    title,
    titleAction,
    searchPlaceholder,
    emptyText,
    clearLabel = 'Clear',
    className,
    autoFocusSearch = false,
    listClassName = 'scrollbar-hide',
    renderOption,
}: ComboboxProps) {
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
    const optionsKey = options.map((option) => option.value).join('|');

    useEffect(() => {
        setActiveIndex(options.length > 0 ? 0 : -1);
        // Reset the highlighted option whenever the set of options changes
        // (e.g. after searching), keyed by option values to stay stable
        // across renders that produce an equivalent list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsKey]);

    useEffect(() => {
        if (activeIndex < 0) return;
        optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        event.stopPropagation();
        if (options.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((prev) => (prev + 1) % options.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(
                (prev) => (prev - 1 + options.length) % options.length,
            );
        } else if (event.key === 'Enter') {
            const option = options[activeIndex];
            if (option) {
                event.preventDefault();
                onToggle(option.value);
            }
        }
    };

    useEffect(() => {
        if (!autoFocusSearch) return;
        const frame = requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
        return () => cancelAnimationFrame(frame);
    }, [autoFocusSearch]);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        onSearch?.(debouncedSearch);
    }, [debouncedSearch, onSearch]);

    useEffect(() => {
        if (!autoFocus) return;
        const input = searchInputRef.current;
        if (!input) return;

        let frame = 0;
        const focusSearch = () => {
            frame = requestAnimationFrame(() => input.focus());
        };

        // When the combobox lives inside a Radix menu/submenu, the menu keeps
        // pulling focus back to itself: right after opening (its focus trap)
        // and later whenever the pointer enters/leaves menu items (Radix
        // refocuses the menu container). Whenever focus lands back on a menu
        // container or item, send it to the search input. We defer via rAF to
        // avoid re-entrancy with Radix's own focus handling, and we ignore
        // focus that legitimately leaves the menu (e.g. a modal opened from
        // here), so we only fight the menu, never the user.
        const handleFocusIn = (event: FocusEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target || target === input) return;
            const role = target.getAttribute('role');
            if (role === 'menu' || role === 'menuitem') {
                focusSearch();
            }
        };

        focusSearch();
        document.addEventListener('focusin', handleFocusIn, true);
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener('focusin', handleFocusIn, true);
        };
    }, [autoFocus]);

    useInfiniteScroll(scrollContainer, {
        onLoadMore: onLoadMore ?? (() => {}),
        isLoading,
        hasMore,
        enabled: !!onLoadMore,
    });

    const isEmpty = options.length === 0 && !isLoading;

    return (
        <Popover
            className={cn(
                'relative isolate gap-2.5 p-2',
                'w-[280px] max-h-[380px] overflow-hidden',
                className,
            )}
            onKeyDown={handleKeyDown}
        >
            {title && (
                <div className="flex items-center justify-between shrink-0 px-2 h-8">
                    <span className="text-sm-medium text-white-100">
                        {title}
                    </span>
                    <div className="flex items-center gap-2">
                        {selected.length > 0 && onClear && (
                            <button
                                type="button"
                                onClick={onClear}
                                className="text-xs text-grey-200 hover:text-white-100 transition-colors"
                            >
                                {clearLabel}
                            </button>
                        )}
                        {titleAction}
                    </div>
                </div>
            )}
            {searchPlaceholder && (
                <SearchInput
                    ref={searchInputRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    containerClassName="shrink-0"
                />
            )}
            <div
                ref={setScrollContainer}
                className={cn(
                    'flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto',
                    listClassName,
                )}
            >
                {isEmpty && emptyText ? (
                    <div className="py-6 text-center text-xs text-grey-200">
                        {emptyText}
                    </div>
                ) : (
                    options.map((option, index) => {
                        const isSelected = selected.includes(option.value);
                        const isActive = index === activeIndex;
                        return (
                            <button
                                key={option.value}
                                ref={(el) => {
                                    optionRefs.current[index] = el;
                                }}
                                type="button"
                                onClick={() => onToggle(option.value)}
                                onMouseEnter={() =>
                                    setActiveIndex((prev) =>
                                        prev === index ? prev : index,
                                    )
                                }
                                className={cn(
                                    'flex cursor-pointer items-center gap-1 rounded-[5px] text-left outline-none transition-colors hover:bg-grey-550',
                                    isActive && 'bg-grey-550',
                                )}
                            >
                                {renderOption ? (
                                    renderOption(option, { isSelected })
                                ) : (
                                    <DefaultComboboxRow
                                        option={option}
                                        isSelected={isSelected}
                                    />
                                )}
                            </button>
                        );
                    })
                )}
                {isLoading && (
                    <div className="flex justify-center py-2">
                        <Spinner size={16} />
                    </div>
                )}
            </div>
            <ScrollEndFade
                container={scrollContainer}
                contentVersions={[options.length, isLoading]}
                className="rounded-b"
            />
        </Popover>
    );
}

function DefaultComboboxRow({
    option,
    isSelected,
}: {
    option: ComboboxOption;
    isSelected: boolean;
}) {
    const OptionIcon = option.icon;
    return (
        <span className="flex h-8 min-w-0 flex-1 items-center gap-1 py-1.5 pl-2 pr-1.5 text-sm leading-5 text-white-100">
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
                {OptionIcon && (
                    <OptionIcon className="h-4 w-4 shrink-0 text-grey-200" />
                )}
                <span className="flex-1 truncate">{option.label}</span>
            </span>
            {isSelected && (
                <CheckIcon className="h-4 w-4 shrink-0 text-white-100" />
            )}
        </span>
    );
}
